import { getLogLevel, logDebug, LogLevel, type Unsubscribe } from '@amazon/vinyl-util'
import { setClassToken } from '@amazon/vinyl-tsx'
import { data } from '@amazon/vinyl-observable'
import { generateTheme } from '@amzn/amp-image-details'
import { ctx } from '@/util/context'
import { globalData } from '@/util/globalData'

const { target } = ctx('DynamicThemeColors')

/**
 * App-wide image-derived theme. `bindThemeColors` stamps the `bg` seed (the
 * derived `--bg-g1…` wash) and the `accent` seed (the derived `--accent-color-*`
 * / `--secondary-accent-color-*` pairs) on the document root; a null seed clears
 * its vars so `styles.scss` falls back to the default wash / grayscale accent.
 */
export type ThemeColors = {
    readonly bg: string | null
    readonly accent: string | null
}

export const themeColors$ = globalData(() => data<ThemeColors>({ bg: null, accent: null }))

/**
 * The teal brand seed. The global no-image fallback is grayscale (styles.scss);
 * an imageless page that wants the teal brand theme opts in explicitly with
 * `setThemeColors({ …, accent: TEAL_SEED })`, deriving the same OKLCH accent +
 * secondary every image page gets.
 */
export const TEAL_SEED = '#25d1da'

export function setThemeColors(colors: ThemeColors) {
    themeColors$.value = colors
}

/**
 * Reset the app theme to its default — the stylesheet wash + grayscale accent
 * fire. Navigation no longer clears the theme implicitly: the previous page's
 * colors persist until the next page sets or clears them, so a page opts into
 * the grayscale default on unmount rather than leaving the last hero's colors.
 */
export function clearThemeColors() {
    themeColors$.value = { bg: null, accent: null }
}

/**
 * Subscribe `themeColors$` and stamp the image-derived theme on the DOM until
 * the returned `Unsubscribe` runs. Both the bg and accent vars go on
 * `document.documentElement` — where `data-bb-theme` lives, so each resolves
 * against the theme selector and inherits down to the body-level `--bg-g1` /
 * `--chrome-bg` / chat-surface consumers. The radial-paint class toggles on
 * `document.body`, the element that paints the page-background gradient.
 */
export function bindThemeColors(): Unsubscribe {
    return themeColors$.onData(({ bg, accent }) => {
        applyBgColors(document.documentElement, bg)
        setClassToken(document.body, 'backgroundRadial', bg != null)
        applyAccentColors(document.documentElement, accent)
        // ?logLevel=debug — seeds + the derived theme they resolved to, or null on clear.
        if (getLogLevel() <= LogLevel.DEBUG) {
            logDebug(target, 'theme set', {
                bgSeed: bg,
                accentSeed: accent,
                bgTheme: bg ? generateTheme(bg) : null,
                accentTheme: accent ? generateTheme(accent) : null,
            })
        }
    })
}

function setVar(target: HTMLElement, prop: string, value: string | undefined) {
    if (value) target.style.setProperty(prop, value)
    else target.style.removeProperty(prop)
}

/**
 * Stamp the derived page-background wash from a seed color, or clear every
 * `--bg-g{n}-{dark,light}` var on a null seed so `styles.scss` falls back to the
 * default wash. All three slots share the single derived bg per theme.
 */
export function applyBgColors(target: HTMLElement, seed: string | null) {
    const pair = seed ? generateTheme(seed) : null
    for (let i = 1; i <= 3; i++) {
        setVar(target, `--bg-g${i}-dark`, pair?.dark.bg)
        setVar(target, `--bg-g${i}-light`, pair?.light.bg)
    }
}

/**
 * Stamp the derived primary + secondary accent pairs from a seed color, or clear
 * all four vars on a null seed so the stylesheet's grayscale default accent
 * fires. `styles.scss` resolves `--accent-color` / `--secondary-accent-color`
 * from the theme-correct half; consumers use those contrast-nudged pairs directly.
 */
export function applyAccentColors(target: HTMLElement, seed: string | null) {
    const pair = seed ? generateTheme(seed) : null
    setVar(target, '--accent-color-dark', pair?.dark.accent)
    setVar(target, '--accent-color-light', pair?.light.accent)
    setVar(target, '--secondary-accent-color-dark', pair?.dark.secondary)
    setVar(target, '--secondary-accent-color-light', pair?.light.secondary)
}
